/**
 * Connection Tab - WebSocket connection status and controls
 */

import { IconPlugConnected, IconPlugConnectedX } from '@tabler/icons-react';
import { Stack, Group, Button, Paper, Divider, Badge, Text, Card } from '../../../../components/ui';
import { SlideIn } from '../../../../components/animations';
import { usePresenceManager } from '../../../../hooks/usePresenceManager';
import { getWsUrl } from '../../../../config/ccms';
import { JWTDebugger } from './JWTDebugger';

export function ConnectionTab() {
  const { manager, isConnected, connectionState, config } = usePresenceManager();

  return (
    <Stack gap="md">
      {/* Connection Status Card */}
      <Card variant="gradient" gradientVariant={isConnected ? 'success' : 'danger'}>
        <SlideIn direction="up">
          <Stack gap="xs">
            <Group gap="xs">
              {isConnected ? (
                <IconPlugConnected size={16} color="rgba(255, 255, 255, 0.9)" />
              ) : (
                <IconPlugConnectedX size={16} color="rgba(255, 255, 255, 0.9)" />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                Connection Status
              </Text>
            </Group>
            <Text size="xl" fw={700} c="white">
              {isConnected ? 'Connected' : 'Disconnected'}
            </Text>
          </Stack>
        </SlideIn>
      </Card>

      {/* Connection Details */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Connection Details
          </Text>
          <Divider />

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Hub URL
            </Text>
            <Text size="sm">{getWsUrl('presence')}</Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              State
            </Text>
            <Badge color={isConnected ? 'green' : 'red'}>{connectionState}</Badge>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Auto-Heartbeat
            </Text>
            <Badge color={config.autoHeartbeat ? 'blue' : 'gray'}>
              {config.autoHeartbeat ? 'Enabled' : 'Disabled'}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Heartbeat Interval
            </Text>
            <Text size="sm">{config.heartbeatInterval}s</Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Activity Tracking
            </Text>
            <Badge color={config.activityTracking ? 'blue' : 'gray'}>
              {config.activityTracking ? 'Enabled' : 'Disabled'}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Idle Detection
            </Text>
            <Badge color={config.idleDetection ? 'blue' : 'gray'}>
              {config.idleDetection ? 'Enabled' : 'Disabled'}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Idle Timeout
            </Text>
            <Text size="sm">{config.idleTimeout} minutes</Text>
          </Group>
        </Stack>
      </Paper>

      {/* Connection Controls */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Connection Controls
          </Text>
          <Divider />
          <Group>
            <Button
              leftSection={<IconPlugConnected size={16} />}
              onClick={() => manager?.connect()}
              disabled={isConnected}
              variant="filled"
            >
              Connect
            </Button>
            <Button
              leftSection={<IconPlugConnectedX size={16} />}
              onClick={() => manager?.disconnect()}
              disabled={!isConnected}
              variant="outline"
              color="red"
            >
              Disconnect
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* JWT Token Debugger */}
      <JWTDebugger />
    </Stack>
  );
}
